import { DEFAULT_INITIAL_PAGE } from '@/constants';
import { fetchData } from '@/services';
import { selectUid } from '@/store/app/selectors';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

type UseGetMoreItemsProps<T> = {
  initialItems: T[];
  fetchUrl: string;
  pagesCount: number;
};

type UseGetMoreItemsReturn<T> = {
  items: T[];
  isLoading: boolean;
  hasMore: boolean;
  handleLoadMore: () => Promise<void>;
};

export const useGetMoreItems = <T>({
  initialItems,
  fetchUrl,
  pagesCount,
}: UseGetMoreItemsProps<T>): UseGetMoreItemsReturn<T> => {
  const [items, setItems] = useState<T[]>(initialItems);
  const [page, setPage] = useState(DEFAULT_INITIAL_PAGE);
  const [isLoading, setIsLoading] = useState(false);
  const hasMorePages = !!pagesCount && page < pagesCount;
  const [hasMore, setHasMore] = useState(hasMorePages ?? false);
  const uid = useSelector(selectUid);
  const isResettingRef = useRef(false);

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  useEffect(() => {
    if (uid) {
      const timeoutId = setTimeout(() => {
        resetItems();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [fetchUrl]);

  const handleLoadMore = useCallback(async () => {
    if (!hasMorePages || isResettingRef.current) return;
    const nextPage = page + 1;

    setIsLoading(true);

    const urlWithPagination = `${fetchUrl}&page=${nextPage}`;
    const response = await fetchData({
      url: urlWithPagination,
    });

    if (response.success && !isResettingRef.current) {
      const { list: newItems, pagination } = response.data;
      setHasMore(pagination.page < pagination.countPages);
      setItems((prev) => [...prev, ...newItems]);
      setPage(nextPage);
    }

    setIsLoading(false);
  }, [hasMorePages, page, fetchUrl]);

  const resetItems = async () => {
    isResettingRef.current = true;
    setIsLoading(true);

    const urlWithPagination = `${fetchUrl}&page=${DEFAULT_INITIAL_PAGE}`;
    const response = await fetchData({
      url: urlWithPagination,
    });

    if (response.success) {
      const { list: items, pagination } = response.data;
      setHasMore(pagination.page < pagination.countPages);
      setItems(items);
      setPage(DEFAULT_INITIAL_PAGE);
    }

    setIsLoading(false);
    isResettingRef.current = false;
  };

  return {
    items,
    isLoading,
    hasMore,
    handleLoadMore,
  };
};
